import { useEffect, useRef, useState } from 'react';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';

let loadPromise: Promise<void> | null = null;

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  // Already loaded
  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  // Already loading
  if (loadPromise) {
    return loadPromise;
  }

  // Already in DOM (e.g. from a previous hot-reload)
  if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
    loadPromise = new Promise((resolve) => {
      const check = () => {
        if (window.google?.maps?.places) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Dynamically loads Google Maps JS SDK with the Places library.
 * Returns `isLoaded` once the SDK is ready, and `error` if loading fails.
 * The script is loaded only once and shared across all hook consumers.
 */
export function useGoogleMapsLoader() {
  const [isLoaded, setIsLoaded] = useState(!!window.google?.maps?.places);
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (isLoaded || attempted.current) return;
    attempted.current = true;

    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
    if (!apiKey) {
      setError('VITE_GOOGLE_PLACES_API_KEY is not configured');
      return;
    }

    loadGoogleMapsScript(apiKey)
      .then(() => setIsLoaded(true))
      .catch((err) => setError(err.message));
  }, [isLoaded]);

  return { isLoaded, error };
}
